import React, { useState, useEffect, useRef } from 'react';

// Hooks
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// Actions
import { failure, success } from '~/redux/actions/snackbar-actions';
import {
  fetchContacts,
  fetchContactLabels,
  contactBulkActions,
  fetchDeliverableContacts,
} from '~/redux/actions/contacts-actions';
import { fetchCustomFieldsCatalog } from '~/redux/actions/customFields-actions';
import { CLEAR_CONTACTS_FILTERS } from '~/redux/actions/action-types';

// Utils
import { MESSAGES } from '~/utils/message';
import { DEFAULT_PAGINATION } from '~/utils/constants';
import { debounce } from '~/utils/helperFunctions';

// Components
import Table from './Table';
import AdvanceFilters from './AdvanceFilters';
import NoContactsScreen from './NoContactsScreen/index';
import TagsUpdate from './TagsUpdate';
import CreateContactModal from './CreateContactModal';
import ConfirmModal from '../General/ConfirmModal';
import DeliverableContactsModal from './DeliverableContactsModal';
import LimitModal from '../General/LimitModal';
import OlcActionMenu from '~/components/General/OlcActionMenu';
import OlcButton from '~/components/General/OlcButton';
import OlcTooltip from '~/components/General/OlcTooltip';
import FindNewContactsModal from '~/components/General/FindNewContactsModal';
import OlcSearchInput from '~/components/General/OlcSearchInput';
import AddContactsGuideModal from './AddContactsGuideModal';

// MUI Components
import {
  Container,
  Box,
  Grid,
  Typography,
  useMediaQuery,
  MenuItem,
  CircularProgress,
} from '@mui/material';

// Icons
import {
  ImportCIcon,
  AddCIcon,
  AddDarkCIcon,
  RemoveTagsCIcon,
  DeleteWebhookCIcon,
  Add2CIcon,
  Remove2CIcon,
  Delete2CIcon,
  Export2CIcon,
  Add3CIcon,
  ContactDetailsDeleteCIcon,
  ImportContactsCIcon,
  FindNewContactsCIcon,
  PlaceOrderCIcon,
  ExportModalNewCIcon,
  RemoveDncCIcon,
} from '~/assets/images';
// Styles
import './styles.scss';

const DELIVERABLE_MODAL_INITIAL = {
  open: false,
  deliverableCount: 0,
  nonDeliverableCount: null,
  validContacts: [],
};

const Contacts = () => {
  const [mounted, setIsMounted] = useState(false);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [search, setSearch] = useState('');
  const [selectedRows, setSelectedRows] = useState([]);
  const [searchApplied, setSearchApplied] = useState(false);
  const [tagsUpdateModal, setTagsUpdateModal] = useState({
    open: false,
    type: '',
  });
  const [selectedTags, setSelectedTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [placeOrderLoading, setPlaceOrderLoading] = useState(false);

  const [deliverableModal, setDeliverableModal] = useState(DELIVERABLE_MODAL_INITIAL);
  const closeDeliverableModal = () => setDeliverableModal(DELIVERABLE_MODAL_INITIAL);
  const [openAdvanceFilters, setOpenAdvanceFilters] = useState(false);
  const [openCreateContactModal, setOpenCreateContactModal] = useState(false);
  const [openFindNewContactsModal, setOpenFindNewContactsModal] = useState(false);
  const [openAddContactsGuideModal, setOpenAddContactsGuideModal] = useState(false);
  const [limitModal, setLimitModal] = useState(false);
  const [limitMessage, setLimitMessage] = useState('');

  const [openConfirmModal, setOpenConfirmModal] = useState({
    type: '',
    open: false,
    message: '',
    handleOk: () => {},
    loading: false,
    header: '',
    successButtonName: '',
    icon: '',
    svg: '',
    className: '',
    modalName: '',
    handleClose: () => {},
  });

  // Actions State
  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
  const actionsOpen = Boolean(actionsAnchorEl);
  const handleActionsClick = (event) => {
    setActionsAnchorEl(event.currentTarget);
  };
  const handleActionsClose = () => {
    setActionsAnchorEl(null);
  };
  // Actions State

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const childRef = useRef();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const totalContactsInDB = useSelector((state) => state.contactsReducers.totalContactsInDB);
  // const totalContactsInDB = 0;
  const applyFilters = useSelector((state) => state.contactsReducers.applyFilters);
  const filters = useSelector((state) => state.contactsReducers.filters);
  const filterSize = useSelector((state) => state.contactsReducers.filterSize);
  const allContacts = useSelector((state) => state.contactsReducers.contacts);

  const currentMode = useSelector((state) => state.userReducers.user?.isLiveMode);
  const isTestMode = currentMode === false;

  // Use the useMediaQuery hook to check the screen size
  const isXlScreen = useMediaQuery((theme) => theme.breakpoints.up('xl'));
  // Determine the maxWidth based on the screen size
  const maxWidth = isXlScreen ? 'xxl' : 'xl';

  const handleSearch = () => {
    if (search) {
      setPagination({ ...pagination, page: 0 });
      setSearchApplied(true);
      getContacts();
    }
  };

  const searchKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const debouncedHandleKeyDown = debounce(searchKeyDown, 300);

  const getContacts = () => {
    let payload = {
      pagination,
      search: search.length ? search : null,
      applyFilters: applyFilters,
      filters: applyFilters ? filters : null,
    };
    dispatch(fetchContacts(payload));
  };

  const handleOpenExportModal = () => {
    setOpenConfirmModal({
      type: 'export',
      open: true,
      message:
        selectedRows[0] === -1 || selectedRows.length > 1
          ? MESSAGES.CONTACTS.EXPORT_CONTACTS
          : MESSAGES.CONTACTS.EXPORT_SINGLE_CONTACTS,
      header:
        selectedRows[0] === -1 || selectedRows.length > 1
          ? MESSAGES.CONTACTS.MULTI_EXPORT_HEADER_NEW
          : MESSAGES.CONTACTS.SINGLE_EXPORT_HEADER_NEW,
      description: MESSAGES.CONTACTS.EXPORT_MESSAGE,
      handleOk: exportContacts,
      loading: false,
      successButtonName: MESSAGES.CONTACTS.EXPORT_BUTTON_TEXT,
      icon: ExportModalNewCIcon,
      className: 'exportModalContact',
      modalName: 'exportContacts',
      handleClose: () => {
        setOpenConfirmModal((prev) => ({ ...prev, open: false }));
      },
    });
    handleActionsClose();
  };

  const handleOpenDeleteModal = () => {
    setOpenConfirmModal({
      type: 'delete',
      open: true,
      message:
        selectedRows[0] === -1 || selectedRows.length > 1
          ? MESSAGES.CONTACTS.DELETE_CONTACTS
          : MESSAGES.CONTACTS.DELETE_SINGLE_CONTACTS,
      header:
        selectedRows[0] === -1 || selectedRows.length > 1
          ? MESSAGES.CONTACTS.MULTI_DELETE_HEADER_NEW
          : MESSAGES.CONTACTS.SINGLE_DELETE_HEADER_NEW,
      description: MESSAGES.CONTACTS.DELETE_MESSAGE_NEW,
      handleOk: deleteContacts,
      loading: false,
      successButtonName: MESSAGES.CONTACTS.DELETE.SUBMIT_BUTTON,
      icon: ContactDetailsDeleteCIcon,
      className: 'deleteModal',
      modalName: 'deleteContacts',
      handleClose: () => {
        setOpenConfirmModal((prev) => ({ ...prev, open: false }));
      },
    });
    handleActionsClose();
  };

  const handleOpenRemoveDncModal = () => {
    setOpenConfirmModal({
      type: 'removeDnc',
      open: true,
      header: MESSAGES.CONTACTS.REMOVE_DNC_HEADER,
      description: MESSAGES.CONTACTS.REMOVE_DNC_MESSAGE,
      handleOk: removeDncContacts,
      loading: false,
      successButtonName: MESSAGES.CONTACTS.REMOVE_DNC_BUTTON,
      icon: RemoveTagsCIcon,
      className: 'removeDncModal',
      modalName: 'removeDncContacts',
      handleClose: () => {
        setOpenConfirmModal((prev) => ({ ...prev, open: false }));
      },
    });
    handleActionsClose();
  };

  const exportContacts = async () => {
    try {
      setOpenConfirmModal((prev) => ({ ...prev, loading: true }));
      const payload = {
        ids: selectedRows[0] === -1 ? -1 : selectedRows,
        action: 'export',
        search: search.length ? search : null,
        applyFilters: applyFilters,
        filters: applyFilters ? filters : null,
      };
      const response = await contactBulkActions(payload);
      if (response?.status === 418) {
        setOpenConfirmModal((prev) => ({ ...prev, open: false }));
        setLimitMessage(response?.data?.message || '');
        setLimitModal(true);
        return;
      }
      getContacts();
      setSelectedRows([]);
      setOpenConfirmModal((prev) => ({ ...prev, open: false }));
      dispatch(success(response.data.message));
    } catch (error) {
      dispatch(failure(error?.response?.data?.message || error?.message || MESSAGES.GENERAL_ERROR));
    } finally {
      setTimeout(() => {
        setOpenConfirmModal((prev) => ({ ...prev, loading: false, open: false }));
      }, 2000);
    }
  };

  const deleteContacts = async () => {
    try {
      setOpenConfirmModal((prev) => ({ ...prev, loading: true }));
      const payload = {
        ids: selectedRows[0] === -1 ? -1 : selectedRows,
        action: 'delete',
        search: search.length ? search : null,
        applyFilters: applyFilters,
        filters: applyFilters ? filters : null,
      };
      const response = await contactBulkActions(payload);
      getContacts();
      setSelectedRows([]);
      setOpenConfirmModal((prev) => ({ ...prev, open: false }));
      dispatch(success(response.data.message));
    } catch (error) {
      dispatch(failure(error?.response?.data?.message || error?.message || MESSAGES.GENERAL_ERROR));
    } finally {
      setTimeout(() => {
        setOpenConfirmModal((prev) => ({ ...prev, loading: false, open: false }));
      }, 2000);
    }
  };

  const removeDncContacts = async () => {
    try {
      setOpenConfirmModal((prev) => ({ ...prev, loading: true }));
      const payload = {
        ids: selectedRows[0] === -1 ? -1 : selectedRows,
        action: 'remove dnc',
        search: search.length ? search : null,
        applyFilters: applyFilters,
        filters: applyFilters ? filters : null,
      };
      const response = await contactBulkActions(payload);
      getContacts();
      setSelectedRows([]);
      handleActionsClose();
      setOpenConfirmModal((prev) => ({ ...prev, open: false }));
      dispatch(success(response.data.message));
    } catch (error) {
      dispatch(failure(error?.response?.data?.message || error?.message || MESSAGES.GENERAL_ERROR));
    } finally {
      setOpenConfirmModal((prev) => ({ ...prev, open: false }));
      setTimeout(() => {
        setOpenConfirmModal((prev) => ({ ...prev, loading: false }));
      }, 2000);
    }
  };

  const formatContactsForOrder = () => {
    const selectedContactsData = allContacts.filter((contact) => selectedRows.includes(contact.id));
    return selectedContactsData.map((contact) => ({
      id: contact.id,
      label:
        `${contact?.fullName && contact.fullName !== 'null' ? contact.fullName + ', ' : ''}${contact?.city || ''} ${contact?.state || ''} ${contact?.zip || ''}`.trim(),
    }));
  };

  const handlePlaceOrder = async () => {
    const isSelectAll = selectedRows[0] === -1;
    const formattedContacts = isSelectAll ? [] : formatContactsForOrder();

    if (!isSelectAll && !formattedContacts.length) {
      dispatch(failure('No contacts selected.'));
      return;
    }

    setPlaceOrderLoading(true);
    handleActionsClose();

    try {
      const ids = isSelectAll
        ? -1
        : formattedContacts.map((c) => Number(c.id)).filter((id) => Number.isFinite(id) && id > 0);

      const response = await dispatch(
        fetchDeliverableContacts({
          ids,
          applyFilters,
          filters,
        })
      );

      if (!response || response.status !== 200) {
        const validationMsg = response?.data?.data?.errors?.[0]?.msg;
        const rawMessage =
          validationMsg ||
          response?.data?.message ||
          'Failed to validate contacts. Please try again';
        const message = rawMessage.toLowerCase().includes('given filters')
          ? rawMessage.replace(/given filters/i, 'given selection')
          : rawMessage;
        dispatch(failure(message));
        return;
      }

      // 200: backend returns { message: "Success", data: { deliverableContactIds, totalDeliverableContactsCount } }
      const { deliverableContactIds, totalDeliverableContactsCount } = response.data.data;
      const deliverableCount = totalDeliverableContactsCount ?? deliverableContactIds?.length ?? 0;
      const nonDeliverableCount = isSelectAll ? null : formattedContacts.length - deliverableCount;

      const validContacts = isSelectAll
        ? deliverableContactIds.map((id) => ({ id: String(id) }))
        : formattedContacts.filter((c) =>
            new Set(deliverableContactIds.map(Number)).has(Number(c.id))
          );

      setDeliverableModal({
        open: true,
        deliverableCount,
        nonDeliverableCount,
        validContacts,
      });
    } catch {
      dispatch(failure('Failed to validate contacts. Please try again.'));
    } finally {
      setPlaceOrderLoading(false);
    }
  };

  const handleDeliverableModalContinue = () => {
    const { validContacts } = deliverableModal;
    closeDeliverableModal();
    navigate('/orders/create-order', {
      state: { selectedContactIds: validContacts, isMultipleContacts: true },
    });
  };

  useEffect(() => {
    setIsMounted(true);
    getContacts();
    dispatch(fetchContactLabels());
    return () => {
      dispatch({ type: CLEAR_CONTACTS_FILTERS });
    };
  }, []);

  useEffect(() => {
    if (mounted) {
      getContacts();
    }
  }, [pagination.page, pagination.pageSize, filterSize, filters]);

  useEffect(() => {
    if (!search && mounted) {
      setSearchApplied(false);
      setSearch('');
      getContacts();
    }
  }, [search]);

  useEffect(() => {
    if (mounted) {
      let payload = {
        DEFAULT_PAGINATION,
        search: search.length ? search : null,
      };
      dispatch(fetchContacts(payload));
      dispatch(fetchContactLabels());
      dispatch(fetchCustomFieldsCatalog());
    }
  }, [currentMode]);

  const noContactsInDb = () => {
    return (
      <Container maxWidth={maxWidth} className="olcEmptyStatePageContainer">
        <NoContactsScreen
          setOpenCreateContactModal={setOpenCreateContactModal}
          openCreateContactModal={openCreateContactModal}
          findNewContactsDisabled={isTestMode}
          findNewContactsDisabledMessage={MESSAGES.MODE.LIVE_MODE_ONLY_FEATURE}
          onFindNewContacts={() => {
            if (isTestMode) return;

            setOpenFindNewContactsModal(true);
          }}
        />
        <FindNewContactsModal
          isDisabled={isTestMode}
          disabledMessage={MESSAGES.MODE.LIVE_MODE_ONLY_FEATURE}
          open={openFindNewContactsModal}
          onClose={() => setOpenFindNewContactsModal(false)}
        />
      </Container>
    );
  };

  if (totalContactsInDB < 1 && totalContactsInDB !== null) {
    return noContactsInDb();
  }

  return (
    <Container maxWidth={maxWidth} className="contactsPageContainer olcPrimaryListPage">
      <Grid container columnSpacing={1} className="contactsToolbarRow">
        <Grid
          item
          lg={6}
          md={filterSize > 0 && selectedRows.length > 0 ? 8 : 6}
          sm={12}
          xs={12}
          className="searchBtnWrapper newSearchBtnWrapper"
        >
          <Box className="searchWrapper" sx={{ height: '40px', width: '100%' }}>
            <OlcSearchInput
              searchApplied={searchApplied}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value.trimStart())}
              onKeyDown={debouncedHandleKeyDown}
              placeholder={MESSAGES.CONTACTS.SEARCH_PLACE_HOLDER}
              title={MESSAGES.CONTACTS.SEARCH_PLACE_HOLDER}
              onClear={() => {
                setSearch('');
                setSearchApplied(false);
              }}
            />
            <Box className="filterBtnWrapper">
              <OlcButton
                variant="secondary"
                onClick={() => {
                  setOpenAdvanceFilters(true);
                }}
              >
                Filters
              </OlcButton>
              {filterSize > 0 && <Typography className="filtersCounter">{filterSize}</Typography>}
            </Box>
            {filterSize > 0 && (
              <OlcButton
                variant="secondary"
                onClick={() => {
                  childRef.current.clearFilters();
                }}
                className="clearFilterBtn"
              >
                {MESSAGES.CONTACTS.CLEAR_FILTER_BTN_TEXT}
              </OlcButton>
            )}
          </Box>
        </Grid>
        <Grid
          item
          lg={6}
          md={filterSize > 0 && selectedRows.length > 0 ? 4 : 6}
          sm={12}
          xs={12}
          className="contactsActionsGrid"
          sx={{
            alignItems: 'flex-start',
          }}
        >
          <Box className="contactActionsBtnWrapper">
            {/* Actions Start */}
            {selectedRows.length > 0 && (
              <>
                <OlcButton
                  variant="secondary"
                  id="contact-bulk-actions-button"
                  aria-controls={actionsOpen ? 'contact-bulk-actions-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={actionsOpen ? 'true' : undefined}
                  onClick={handleActionsClick}
                  dropdown
                  expanded={actionsOpen}
                >
                  {MESSAGES.CONTACTS.ACTIONS_BTN_TEXT}
                </OlcButton>
                <OlcActionMenu
                  id="contact-bulk-actions-menu"
                  anchorEl={actionsAnchorEl}
                  open={actionsOpen}
                  onClose={handleActionsClose}
                  MenuListProps={{
                    'aria-labelledby': 'contact-bulk-actions-button',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  sx={{
                    marginTop: '4px',
                  }}
                >
                  <MenuItem
                    className="menuItem"
                    disabled={placeOrderLoading}
                    onClick={handlePlaceOrder}
                  >
                    {placeOrderLoading ? (
                      <CircularProgress size={14} sx={{ color: '#ed5c2f', mr: '6px' }} />
                    ) : (
                      <img src={PlaceOrderCIcon} alt="icon" />
                    )}
                    {MESSAGES.CONTACTS.ACTIONS.PLACE_ORDER}
                  </MenuItem>
                  <MenuItem
                    className="menuItem"
                    onClick={() => {
                      setTagsUpdateModal({
                        open: true,
                        type: 'update',
                      });
                      handleActionsClose();
                    }}
                  >
                    <img src={Add2CIcon} alt="icon" />
                    {MESSAGES.CONTACTS.ACTIONS.ADD_TAGS}
                  </MenuItem>
                  <MenuItem
                    className="menuItem"
                    onClick={() => {
                      setTagsUpdateModal({
                        open: true,
                        type: 'remove',
                      });
                      handleActionsClose();
                    }}
                  >
                    <img src={Remove2CIcon} alt="icon" />
                    {MESSAGES.CONTACTS.ACTIONS.REMOVE_TAGS}
                  </MenuItem>
                  <MenuItem className="menuItem" onClick={handleOpenDeleteModal}>
                    <img src={Delete2CIcon} alt="icon" />
                    {MESSAGES.CONTACTS.ACTIONS.DELETE_CONTACTS}
                  </MenuItem>
                  <MenuItem className="menuItem" onClick={handleOpenExportModal}>
                    <img src={Export2CIcon} alt="icon" />
                    {MESSAGES.CONTACTS.ACTIONS.EXPORT_CONTACTS}
                  </MenuItem>
                  <MenuItem
                    className="menuItem"
                    onClick={() => {
                      handleOpenRemoveDncModal();
                      handleActionsClose();
                    }}
                  >
                    <img src={RemoveDncCIcon} alt="icon" />
                    {MESSAGES.CONTACTS.ACTIONS.REMOVE_DNC}
                  </MenuItem>
                </OlcActionMenu>
              </>
            )}
            {/* Actions End */}
            <OlcButton
              variant="primary"
              id="contact-create-button"
              aria-controls={open ? 'contact-create-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
              onClick={handleClick}
              startIcon={<img src={Add3CIcon} alt="" />}
              dropdown
              expanded={open}
            >
              {MESSAGES.CONTACTS.CREATE_BTN_TEXT}
            </OlcButton>
            <OlcActionMenu
              id="contact-create-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              MenuListProps={{
                'aria-labelledby': 'contact-create-button',
              }}
              sx={{
                marginTop: '10px',
              }}
            >
              <MenuItem
                className="menuItem"
                onClick={() => {
                  setOpenCreateContactModal(true);
                  handleClose();
                }}
              >
                <img src={Add2CIcon} alt="icon" />
                Create Contact
              </MenuItem>
              <MenuItem
                className="menuItem"
                onClick={() => {
                  navigate('/contacts/import?origin=contacts');
                }}
              >
                <img src={ImportContactsCIcon} alt="icon" />
                Import Contacts
              </MenuItem>
              <OlcTooltip
                arrow
                disableInteractive={!isTestMode}
                placement="left"
                title={isTestMode ? MESSAGES.MODE.LIVE_MODE_ONLY_FEATURE : ''}
              >
                <li>
                  <MenuItem
                    component="div"
                    className="menuItem"
                    disabled={isTestMode}
                    onClick={() => {
                      if (isTestMode) return;

                      setOpenAddContactsGuideModal(true);
                      handleClose();
                    }}
                  >
                    <img src={FindNewContactsCIcon} alt="icon" />
                    Find New Contacts
                  </MenuItem>
                </li>
              </OlcTooltip>
            </OlcActionMenu>
          </Box>
        </Grid>
      </Grid>
      <Table
        pagination={pagination}
        updatePagination={setPagination}
        setSelectedRows={setSelectedRows}
        selectedRows={selectedRows}
        searchApplied={searchApplied}
        totalContactsInDB={totalContactsInDB}
        filterSize={filterSize}
      />
      <TagsUpdate
        open={tagsUpdateModal.open}
        type={tagsUpdateModal.type}
        handleClose={() => {
          setTagsUpdateModal({
            open: false,
            type: '',
          });
          setSelectedTags([]);
        }}
        selectedTags={selectedTags}
        setSelectedTags={setSelectedTags}
        header={
          tagsUpdateModal.type === 'update'
            ? MESSAGES.CONTACTS.TAGS_UPDATE_TEXT
            : tagsUpdateModal.type === 'remove'
              ? MESSAGES.CONTACTS.TAGS_REMOVE_TEXT
              : ''
        }
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        search={search}
        applyFilters={applyFilters}
        filters={filters}
        pagination={pagination}
      />
      <ConfirmModal openConfirmModal={openConfirmModal} />
      <LimitModal
        open={limitModal}
        onClose={() => {
          setLimitModal(false);
          setTimeout(() => setLimitMessage(''), 100);
        }}
        message={limitMessage}
        resourceType="export"
      />
      <DeliverableContactsModal
        open={deliverableModal.open}
        deliverableCount={deliverableModal.deliverableCount}
        nonDeliverableCount={deliverableModal.nonDeliverableCount}
        onContinue={handleDeliverableModalContinue}
        onClose={closeDeliverableModal}
      />
      <AdvanceFilters
        open={openAdvanceFilters}
        handleCloseFilter={() => {
          setOpenAdvanceFilters(false);
        }}
        updatePagination={setPagination}
        ref={childRef}
      />
      <CreateContactModal
        open={openCreateContactModal}
        handleClose={() => {
          setOpenCreateContactModal(false);
          getContacts();
        }}
      />
      <FindNewContactsModal
        isDisabled={isTestMode}
        disabledMessage={MESSAGES.MODE.LIVE_MODE_ONLY_FEATURE}
        open={openFindNewContactsModal}
        onClose={() => setOpenFindNewContactsModal(false)}
      />
      <AddContactsGuideModal
        findNewContactsDisabled={isTestMode}
        findNewContactsDisabledMessage={MESSAGES.MODE.LIVE_MODE_ONLY_FEATURE}
        open={openAddContactsGuideModal}
        onClose={() => setOpenAddContactsGuideModal(false)}
      />
    </Container>
  );
};

export default Contacts;
